import { unsetRating } from '../model/access';
import type { KinklistDoc } from '../model/types';

export interface SummaryLabels {
  heading: (title: string) => string;
  comments: string;
}

const DEFAULT_LABELS: SummaryLabels = {
  heading: (title) => (title.trim() === '' ? 'Kinklist' : `${title.trim()}'s Kinklist`),
  comments: 'Comments',
};

/**
 * A Markdown summary for pasting into chats: one section per rating (in
 * legend order, unset omitted), listing "Category: kink (role), …", then the
 * comments.
 */
export function textSummary(doc: KinklistDoc, labels: SummaryLabels = DEFAULT_LABELS): string {
  const unsetId = unsetRating(doc.ratings).id;
  const lines = [`**${labels.heading(doc.title)}**`];
  for (const rating of doc.ratings) {
    if (rating.id === unsetId) continue;
    const entries = doc.categories.flatMap((category) => {
      const items = category.kinks.flatMap((kink) => {
        const roles = category.roles.filter((role) => kink.ratings[role.id] === rating.id);
        if (roles.length === 0) return [];
        const all = roles.length === category.roles.length;
        return [
          all || category.roles.length === 1
            ? kink.name
            : `${kink.name} (${roles.map((r) => r.name).join(', ')})`,
        ];
      });
      return items.length === 0 ? [] : [`- ${category.name}: ${items.join(', ')}`];
    });
    if (entries.length > 0) lines.push('', `**${rating.name}**`, ...entries);
  }
  const comments = doc.categories.flatMap((category) =>
    category.kinks.flatMap((kink) =>
      kink.comment === undefined
        ? []
        : [`- ${category.name} / ${kink.name}: ${kink.comment.replace(/\s*\n\s*/g, ' ')}`],
    ),
  );
  if (comments.length > 0) lines.push('', `**${labels.comments}**`, ...comments);
  return `${lines.join('\n')}\n`;
}
